import { useCallback, useEffect, useRef } from 'react';

import { useMytaverse } from '@providers/MytaverseProvider';

import { previewFinishesInUnreal } from './mapEditorFinishUnreal';
import type { MapLayoutFinishSet } from './mapEditorUnreal';

// How often a drag is allowed to reach the wire.
//
// A preview is client-local in the sense that matters — nothing replicates and nothing is published
// — but it is not free: the hub has no data channel to the instance, so every message travels the
// app WebSocket through the backend and back, on a socket shared with chat, the assistant and
// participant traffic. Per-frame sending would put sixty of those a second on it. A hundred
// milliseconds is under the perceptual threshold for "the wall followed my slider" and is ten
// messages a second at the very worst, which is the ceiling the authority is happy with.
export const FINISH_PREVIEW_THROTTLE_MS = 100;

export interface UseFinishPreviewResult {
  // Call on every change while a control is being dragged. Coalesces: the latest state wins and the
  // ones it overtook are never sent.
  schedulePreview: (finishes: MapLayoutFinishSet) => void;
  // Call when the control is released. Sends immediately, so the world always ends up agreeing with
  // the slider even when the last throttled tick was overtaken.
  commitPreview: (finishes: MapLayoutFinishSet) => void;
}

/**
 * Drives the live finish preview: smooth locally, rationed on the wire.
 *
 * Two rules make this safe on a shared socket. Sends are throttled and coalesced, so a drag costs at
 * most ten messages a second however fast the pointer moves. And a send with the socket closed is
 * DROPPED rather than queued — `react-use-websocket`'s `sendJsonMessage` keeps messages by default
 * and flushes them on reconnect, which is right for a publish and wrong for a stream of superseded
 * previews: after a blip the whole drag would replay into the world, frame by frame, in order.
 *
 * @returns `schedulePreview` for a drag in progress and `commitPreview` for its release.
 */
export const useFinishPreview = (): UseFinishPreviewResult => {
  const { sendMessageToUnreal, isWebsocketOpen } = useMytaverse();

  const sendRef = useRef(sendMessageToUnreal);
  sendRef.current = sendMessageToUnreal;
  const isOpenRef = useRef(isWebsocketOpen);
  isOpenRef.current = isWebsocketOpen;

  const pendingRef = useRef<MapLayoutFinishSet | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentAtRef = useRef(0);
  // The last state this hook wanted the world to be in, whether or not it got there. What the
  // reconnect below re-sends: dropping a preview is only safe if something puts the world right
  // afterwards, and nothing else will — a finish edit reaches UE through this hook alone.
  const desiredRef = useRef<MapLayoutFinishSet | null>(null);

  const flush = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) {
      return;
    }

    desiredRef.current = pending;

    // Dropped, not queued. A preview describes the whole state, so there is nothing in a superseded
    // one worth keeping — and sendJsonMessage would keep every one of them and replay the drag.
    if (!isOpenRef.current) {
      return;
    }

    lastSentAtRef.current = Date.now();
    previewFinishesInUnreal(sendRef.current, pending);
  }, []);

  // Puts the world back in step after the socket comes back.
  //
  // The pair with the drop above: without it a drag that happened across a blip leaves the panel
  // describing one thing and the world showing another, with nothing to correct it until the author
  // happens to touch another control. Sent once per reconnect, and only where something was actually
  // dropped — a reconnect with nothing outstanding is silent.
  const wasOpenRef = useRef(isWebsocketOpen);
  useEffect(() => {
    const cameBack = isWebsocketOpen && !wasOpenRef.current;
    wasOpenRef.current = isWebsocketOpen;

    if (!cameBack || !desiredRef.current) {
      return;
    }

    lastSentAtRef.current = Date.now();
    previewFinishesInUnreal(sendRef.current, desiredRef.current);
  }, [isWebsocketOpen]);

  const schedulePreview = useCallback(
    (finishes: MapLayoutFinishSet) => {
      pendingRef.current = finishes;

      // Leading edge: the first move of a slider reaches the world immediately, and only the ones
      // inside the window behind it wait. A trailing-only throttle makes the first touch feel dead.
      const sinceLast = Date.now() - lastSentAtRef.current;
      if (sinceLast >= FINISH_PREVIEW_THROTTLE_MS) {
        flush();
        return;
      }

      // A tick is already booked. It will pick up whatever is pending when it fires, which is how
      // the intermediate values get dropped rather than queued.
      if (timerRef.current) {
        return;
      }

      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        flush();
      }, FINISH_PREVIEW_THROTTLE_MS - sinceLast);
    },
    [flush],
  );

  const commitPreview = useCallback(
    (finishes: MapLayoutFinishSet) => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      pendingRef.current = finishes;
      flush();
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    },
    [],
  );

  return { schedulePreview, commitPreview };
};
